import { useCallback, useMemo } from 'react';
import {
  ButtonVariants,
  Form,
  FormSizes,
  Modal,
  ModalSizes,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
  useFormState,
} from '@carboledger/ui';
import { DependentDropdownField, FacilityField, TextField } from '@/components/common';
import { buildFacilityOptions, buildLookupOptions } from '@/config/formOptions';
import { useGetCustomerShippingDetailsQuery } from '@/lib/api/lookupsApi';
import { formatNumber } from '@/lib/format/number';
import { useCurrentUser, useTranslations } from '@/hooks';
import { EMPTY_LIST, isFormDirty } from '@/lib/utils';
import {
  useCreateCustomerTransportationMutation,
  useUpdateCustomerTransportationMutation,
} from '../api/customersApi';
import {
  EMPTY_TRANSPORT_FORM,
  REQUIRED_TRANSPORT_FIELDS,
  TRANSPORT_FIELDS,
  TRANSPORT_MODES,
  TRANSPORT_MODE_FIELDS,
  TRANSPORT_MODE_LABEL_KEYS,
  transportFieldName,
} from '../constants/transportation.constants';
import { transportationValidation } from '../customersValidation';
import {
  hasRequiredFields,
  hasTransportDetail,
  totalTransportEmissions,
} from '../utils/customersUtils';
import styles from './TransportationFormModal.module.scss';

const MODE_FIELD_NAMES = TRANSPORT_MODES.flatMap((mode) =>
  Object.values(TRANSPORT_MODE_FIELDS).map((field) => transportFieldName(mode, field)),
);

/**
 * Props:
 *   mode  the transportation_type this row's two fields belong to
 */
const TransportModeFields = ({ mode }) => {
  const t = useTranslations();

  return (
    <div className={styles.mode}>
      <Typography
        variant={TypographyVariants.bodySm}
        weight={TypographyWeights.semibold}
        color={TypographyColors.primary}
      >
        {t(TRANSPORT_MODE_LABEL_KEYS[mode])}
      </Typography>
      <Form.Row columns={2}>
        <TextField
          name={transportFieldName(mode, TRANSPORT_MODE_FIELDS.distance)}
          label={t('FORM_DISTANCE_BETWEEN_SITES')}
          placeholder={t('FORM_DISTANCE_BETWEEN_SITES_PLACEHOLDER')}
          rules={transportationValidation.distance}
        />
        <TextField
          name={transportFieldName(mode, TRANSPORT_MODE_FIELDS.emissionFactor)}
          label={t('FORM_EMISSION_FACTOR')}
          placeholder={t('FORM_EMISSION_FACTOR_PLACEHOLDER')}
          rules={transportationValidation.emissionFactor}
        />
      </Form.Row>
    </div>
  );
};

/**
 * Props:
 *   customerId  scopes "Send to" to this customer's shipping sites
 */
const TransportationFields = ({ customerId }) => {
  const t = useTranslations();
  const user = useCurrentUser();
  const { values } = useFormState();

  const { data: shippingSites = EMPTY_LIST, isSuccess } =
    useGetCustomerShippingDetailsQuery(customerId);

  const facilityOptions = useMemo(
    () => buildFacilityOptions({ sites: user?.sites ?? EMPTY_LIST }),
    [user?.sites],
  );
  const sentToOptions = useMemo(() => buildLookupOptions(shippingSites), [shippingSites]);

  const total = useMemo(() => totalTransportEmissions(values), [values]);
  const totalText = total == null ? '' : formatNumber(total, { maximumFractionDigits: 6 });

  const showModeError =
    !hasTransportDetail(values) &&
    MODE_FIELD_NAMES.some((name) => String(values[name] ?? '').trim() !== '');

  return (
    <div className={styles.fields}>
      <TextField
        required
        name={TRANSPORT_FIELDS.routeName}
        label={t('FORM_ROUTE_NAME')}
        placeholder={t('FORM_ROUTE_NAME_PLACEHOLDER')}
      />

      <Form.Row columns={2}>
        <FacilityField options={facilityOptions} label={t('FORM_SHIPPING_FROM')} />
        <DependentDropdownField
          name={TRANSPORT_FIELDS.sentTo}
          label={t('FORM_SEND_TO')}
          placeholder={t('FORM_SEND_TO_PLACEHOLDER')}
          emptyMessage={t('FORM_SHIPPING_TO_EMPTY')}
          options={sentToOptions}
          ready={isSuccess}
        />
      </Form.Row>

      <div className={styles.sectionHeader}>
        <Typography
          variant={TypographyVariants.label}
          weight={TypographyWeights.semibold}
          color={TypographyColors.secondary}
        >
          {t('SECTION_TRANSPORTATION_DETAILS')}
        </Typography>
      </div>

      {TRANSPORT_MODES.map((mode) => (
        <TransportModeFields key={mode} mode={mode} />
      ))}

      {showModeError ? (
        <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.error}>
          {t('VALIDATION_TRANSPORT_DETAIL_REQUIRED')}
        </Typography>
      ) : null}

      <TextInput
        disabled
        name="totalTransportationEmissions"
        label={t('FORM_TOTAL_TRANSPORTATION_EMISSIONS')}
        value={totalText}
      />
    </div>
  );
};

/**
 * Props:
 *   open        whether the modal is shown
 *   onClose     close handler (✕ / Cancel)
 *   customerId  the customer the route delivers to
 *   route       the route being edited ({ id, formValues }); omit to add a new one
 */
export const TransportationFormModal = ({ open, onClose, customerId, route }) => {
  const t = useTranslations();
  const [createRoute, { isLoading: isCreating }] = useCreateCustomerTransportationMutation();
  const [updateRoute, { isLoading: isUpdating }] = useUpdateCustomerTransportationMutation();

  const isEdit = Boolean(route);
  const saving = isCreating || isUpdating;

  const initialValues = useMemo(
    () => route?.formValues ?? EMPTY_TRANSPORT_FORM,
    [route?.formValues],
  );

  const handleSubmit = useCallback(
    async (values) => {
      const result = isEdit
        ? await updateRoute({ customerId, id: route.id, values })
        : await createRoute({ customerId, values });
      if (result.error) return;
      onClose();
    },
    [isEdit, customerId, route?.id, createRoute, updateRoute, onClose],
  );

  if (!open || !customerId) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values, isValid, submit }) => {
        // Edit saves only a real change; add saves only a complete form.
        const canSave =
          isValid &&
          hasRequiredFields(values, REQUIRED_TRANSPORT_FIELDS) &&
          hasTransportDetail(values) &&
          (!isEdit || isFormDirty(values, initialValues));

        return (
          <Modal
            open
            size={ModalSizes.medium}
            title={
              isEdit ? t('CUSTOMER_TRANSPORT_EDIT_TITLE') : t('CUSTOMERS_ACTION_ADD_TRANSPORTATION')
            }
            onClose={onClose}
            buttons={[
              { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
              {
                label: t('COMMON_SAVE'),
                variant: ButtonVariants.primary,
                disabled: saving || !canSave,
                onClick: submit,
              },
            ]}
          >
            <TransportationFields customerId={customerId} />
          </Modal>
        );
      }}
    </Form>
  );
};
